import { Dialog, Classes, H5, H6, Icon, Intent, Tag, Spinner, Button, Alert } from "@blueprintjs/core";
import React, { useCallback, useEffect, useState } from "react";
import { useProcessStatus } from "src/hooks/useProcessStatus";
import { useSendProcessMessage } from "src/hooks/useSendMessage";

import { Instruction } from "src/models/Instruction";

import { PROCESS_UPDATE } from "src/operations/subscriptions/processStatusUpdate";
import { localTime } from "src/utils/utils";
import { ProcessMessageIndexType, ProcessMessageInput } from "src/__generated__/globalTypes";

import "./TemplateRunningView.scss";

type Props = {
    isOpen: boolean;
    onClose: () => void;
    name: string;
    processId: string;
    companySysId: string;
    requestSysId: string;
    instructions: Instruction[] | undefined | null;
    lastRun?: any;
    refetchLastRun: any,
    setRunStarting: React.Dispatch<React.SetStateAction<boolean>>
};

export default function TemplateRunningView({
    name,
    processId,
    companySysId,
    requestSysId,
    instructions,
    isOpen,
    onClose,
    lastRun,
    refetchLastRun,
    setRunStarting
}: Props) {
    console.log("requestId: ", requestSysId);
    // const [createdOn, setCreatedOn] = useState<number>(0);
    const [startingRun, setStartingRun] = useState(false);
    const [updatedOn, setUpdatedOn] = useState<number>(0);
    const [runState, setRunState] = useState<string>("");

    useEffect(() => {
        // last run is used to show users the runs that happen
        // before a new run is kicked off.
        // at the moment need to refetch to get the latest time information
        //todo: get this information for process Status query
        if (lastRun && lastRun.processState) {
            console.log("getting last run information");
            setRunState(lastRun.processState);
            // setCreatedOn(lastRun.createdOn);
            setUpdatedOn(lastRun.lastUpdatedOn);
        }
    }, [lastRun]);
    const [instructionStates, setInstructionStates] = useState<any[]>([]);

    const [processStatus, processLoading, processError, subscribeToMore] = useProcessStatus(requestSysId);
    console.log("process Status: ", processStatus);
    useEffect(() => {
        if (
            processStatus &&
            processStatus.processAutomationStates &&
            processStatus.processAutomationStates[0] &&
            processStatus.processAutomationStates[0].states
        ) {
            setInstructionStates(processStatus.processAutomationStates[0].states);
            setRunState(processStatus.processState?.state as any);
        }
    }, [processStatus]);

 
    const [approvalBtnStates, setApprovalBtnStates] = useState<{ [instructionId: string]: boolean }>({});

    useEffect(() => {
        const btnStates: { [instructionId: string]: boolean } = {}
        instructionStates.forEach(s => {
            if (s.status === "WaitingForUserApproval") {
                btnStates[s.instructionId] = false
            }
        });
        setApprovalBtnStates(btnStates);

    }, [instructionStates])

    const getInstructionName = useCallback(
        (instructionId: string) => {
            if (instructions) {
                const inst = instructions.find(i => i.instructionId === instructionId);
                if (inst && inst.alias) {
                    return inst.alias;
                }
            }
            return instructionId;
        },
        [instructions]
    );

    const [terminateAlertOpen, setTerminateAlertOpen] = useState(false);
    const [approvalAlertOpen, setApprovalAlertOpen] = useState(false);

    const {
        processMassageMutation: [sendProcessMessage]
    } = useSendProcessMessage();
    const [selectedInstruction, setSelectedInstruction] = useState("");

    const handleDenyConfirm = useCallback(() => {
        const btnStates = { ...approvalBtnStates }
        //disable buttons on click
        btnStates[selectedInstruction] = true;
        setApprovalBtnStates(btnStates)
        const input = createApprovalMessage(
            processId,
            requestSysId,
            companySysId,
            ProcessMessageIndexType.procterminate,
            selectedInstruction
        );
        sendProcessMessage({ variables: { input: input } })
            .then(res => {
                console.log("Terminate Message Sent: ", res);
            })
            .catch(err => {
                console.log("Terminate Error Occured: ", err);
            });
        setTerminateAlertOpen(false);
        setSelectedInstruction("");
    }, [approvalBtnStates, companySysId, processId, requestSysId, selectedInstruction, sendProcessMessage]);

    const handleApprovalConfirm = useCallback(() => {
        const btnStates = { ...approvalBtnStates }
        //disable buttons on click
        btnStates[selectedInstruction] = true;
        setApprovalBtnStates(btnStates)

        const input = createApprovalMessage(
            processId,
            requestSysId,
            companySysId,
            ProcessMessageIndexType.procautoevent,
            selectedInstruction
        );
        sendProcessMessage({ variables: { input: input } })
            .then(res => {
                console.log("Approve Message Sent ", res);
            })
            .catch(err => {
                console.log("Approval  Error: ", err);
            });
        setApprovalAlertOpen(false);
        setSelectedInstruction("");
    }, [approvalBtnStates, companySysId, processId, requestSysId, selectedInstruction, sendProcessMessage]);

    useEffect(() => {

            console.log("subscribing to changes");
            subscribeToMore({
                document: PROCESS_UPDATE,
                variables: { params: { requestSysId }, skip: !requestSysId },
                updateQuery: (prev, { subscriptionData }) => {
                    console.log("prev: ", prev)
                    if (!subscriptionData) return prev;
                    console.log("subscription data: ", subscriptionData);
                    const newdata = subscriptionData.data as any;
                    console.log("newdata: ", newdata);
                    setRunStarting(false);
                    if (newdata.processStatusUpdate.processAutomationStates.length > 0) {
                        console.log("new data: ", newdata);
                        setInstructionStates(newdata.processStatusUpdate.processAutomationStates[0].states);
                        console.log("process run state: ", newdata.processStatusUpdate.processState.state);
                        setRunState(newdata.processStatusUpdate.processState.state);
                        setStartingRun(false);
                        refetchLastRun();
                    }
                }
            });

    }, [lastRun, processId, refetchLastRun, requestSysId, setRunStarting, subscribeToMore]);

    return (
        <>
            <Dialog isOpen={isOpen} onClose={onClose} enforceFocus={false} icon="history" title={name}>
                <div className={Classes.DIALOG_BODY}>
                    <div className="header-row">
                        <H5>Runtime Status:{renderHeaderTag(startingRun ?  "Starting" : runState)}</H5>
                        <div className="header-time">
                            {/* <H6>
                                Created On:
                                <Tag>{localTime(createdOn)}</Tag>
                            </H6> */}
                            <H6>
                                Last Updated:
                                <Tag>{localTime(updatedOn)}</Tag>
                            </H6>
                        </div>
                    </div>

                    {instructionStates.map((i: any) =>
                        renderTag(
                            i.status,
                            i.instructionId,
                            getInstructionName,
                            () => {
                                setTerminateAlertOpen(true);
                                setSelectedInstruction(i.instructionId);

                            },
                            () => {
                                setApprovalAlertOpen(true);
                                setSelectedInstruction(i.instructionId);
                            }, approvalBtnStates
                        )
                    )}
                </div>
                <div className={Classes.DIALOG_FOOTER}>
                    <div className={Classes.DIALOG_FOOTER_ACTIONS}>
                        <Button text="Close" onClick={onClose} />
                    </div>
                </div>
            </Dialog>
            <Alert
                isOpen={terminateAlertOpen}
                intent={Intent.DANGER}
                icon={"issue"}
                cancelButtonText="Cancel"
                confirmButtonText="Deny"
                onCancel={() => setTerminateAlertOpen(false)}
                onConfirm={handleDenyConfirm}
            >
                <p>Are you sure you want to deny approval for this instruction?</p>
            </Alert>
            <Alert
                isOpen={approvalAlertOpen}
                intent={Intent.SUCCESS}
                icon="tick-circle"
                cancelButtonText="Cancel"
                confirmButtonText="Approve"
                onCancel={() => setApprovalAlertOpen(false)}
                onConfirm={handleApprovalConfirm}
            >
                <p>Are you sure you want to approve this instruction?</p>
            </Alert>
        </>
    );
}

type CustomTagProps = {
    className: string;
    text: string;
    intent?: Intent;
    rightIcon?: JSX.Element;
    hasSpinner?: boolean;
    fill?: boolean;
};

function CustomTag({
    className,
    text,
    rightIcon,
    intent = Intent.NONE,
    hasSpinner = false,
    fill = false
}: CustomTagProps) {
    return (
        <Tag className={className} large fill={fill} minimal intent={intent} rightIcon={rightIcon}>
            <div className="approval-text">
                {text}
                {hasSpinner && <Spinner className="spinner" size={15} intent={intent} />}
            </div>
        </Tag>
    );
}

function renderTag(
    status: string,
    instructionId: string,
    getInstructionName: (instructionId: string) => string,
    onReject: (instructionId: string) => void,
    onApprove: (instructionId: string) => void,
    btnStates: { [instructionId: string]: boolean }
) {
    switch (status) {
        case "Completed":
            return (
                <CustomTag
                    className="instruction-tag"
                    text={getInstructionName(instructionId)}
                    fill
                    intent={Intent.SUCCESS}
                    rightIcon={
                        <div>
                            Completed
                            <Icon icon="tick" />
                        </div>
                    }
                />
            );
        case "Running":
            return (
                <CustomTag
                    className="instruction-tag"
                    text={getInstructionName(instructionId)}
                    fill
                    hasSpinner
                    intent={Intent.WARNING}
                    rightIcon={<>Running</>}
                />
            );
        case "Terminated":
            return (
                <CustomTag
                    className="instruction-tag"
                    text={getInstructionName(instructionId)}
                    fill
                    intent={Intent.DANGER}
                    rightIcon={
                        <div>
                            Terminated
                            <Icon icon="cross" />
                        </div>
                    }
                />
            );
        case "Failed":
            return (
                <CustomTag
                    className="instruction-tag"
                    text={getInstructionName(instructionId)}
                    fill
                    intent={Intent.DANGER}
                    rightIcon={
                        <div>
                            Failed
                            <Icon icon="cross" />
                        </div>
                    }
                />
            );
        case "WaitingForUserApproval":
            return (
                <CustomTag
                    className="instruction-tag"
                    text={getInstructionName(instructionId)}
                    fill
                    intent={Intent.WARNING}
                    rightIcon={
                        <div>
                            { btnStates[instructionId] ? "Approval Sent" : "Waiting for Approval"}
                            <Button
                                minimal
                                small
                                icon="tick-circle"
                                intent={Intent.SUCCESS}
                                disabled={btnStates[instructionId]}
                                onClick={() => onApprove(instructionId)}
                            />
                            <Button
                                minimal
                                small
                                icon="delete"
                                intent={Intent.DANGER}
                                disabled={btnStates[instructionId]}
                                onClick={() => onReject(instructionId)}
                            />
                        </div>
                    }
                />
            );
        case "WaitingForDependsOn":
            return (
                <CustomTag
                    className="instruction-tag"
                    text={getInstructionName(instructionId)}
                    fill
                    intent={Intent.DANGER}
                    rightIcon={
                        <div>
                            Terminated
                            <Icon icon="cross" />
                        </div>
                    }
                />
            );
    }
}

function renderHeaderTag(status: string) {
    switch (status) {
        case "Starting":
            return (
                <CustomTag
                    className="header-tag"
                    text="Starting"
                    intent={Intent.SUCCESS}
                    rightIcon={<Icon icon="tick-circle" />}
                />
            );

        case "Completed":
            return (
                <CustomTag
                    className="header-tag"
                    text="Completed"
                    intent={Intent.SUCCESS}
                    rightIcon={<Icon icon="tick-circle" />}
                />
            );
        case "Processing":
            return (
                <CustomTag
                    className="header-tag"
                    text="Processsing"
                    intent={Intent.WARNING}
                    rightIcon={<Spinner size={15} intent={Intent.WARNING} />}
                />
            );
        case "Failed":
            return (
                <CustomTag
                    className="header-tag"
                    text="Failed"
                    intent={Intent.DANGER}
                    rightIcon={<Icon icon="cross" />}
                />
            );
        case "Waiting":
            return <CustomTag className="header-tag" text="Waiting" intent={Intent.WARNING} hasSpinner />;
    }
}

function createApprovalMessage(
    processId: string,
    requestSysId: string,
    companySysId: string,
    indexType: ProcessMessageIndexType,
    instructionId: string
): ProcessMessageInput {
    const eventValue = indexType === ProcessMessageIndexType.procautoevent ? "Approved" : "Terminate";
    return {
        requestSysId: requestSysId,
        processId: processId,
        companySysId: companySysId,
        indexType: ProcessMessageIndexType.procterminate,
        instructions: [
            {
                instructionId: instructionId,
                userInputs: {
                    event: {
                        value: eventValue
                    }
                }
            }
        ]
    };
}
